export const DEFAULT_SKU_UNIT = 'kg'

export interface ParsedSku {
  amount: string
  unit: string
  prefix?: string
}

/** Collapse repeated trailing "kg" suffixes from bad saves. */
function collapseTrailingKg(value: string): string {
  return value.trim().replace(/(\s*kg\s*)+$/gi, ' kg')
}

/**
 * Split stored SKU into optional prefix + numeric weight + unit.
 * Supports "1250 kg", "Big bags 1250 kg", and legacy corrupted "… kg kg kg".
 */
export function parseSku(value: string | null | undefined): ParsedSku {
  if (!value?.trim()) {
    return { amount: '', unit: DEFAULT_SKU_UNIT }
  }

  const trimmed = collapseTrailingKg(value)

  const trailing = trimmed.match(/^(.*?)(\d+(?:\.\d+)?)\s*(kg|kgs?)$/i)
  if (trailing) {
    const prefix = trailing[1].trim()
    return {
      amount: trailing[2],
      unit: DEFAULT_SKU_UNIT,
      ...(prefix ? { prefix } : {}),
    }
  }

  const match = trimmed.match(/^([\d.,]+)\s*([a-zA-Z]+)?$/i)
  if (match) {
    return {
      amount: match[1].replace(/,/g, ''),
      unit: (match[2] || DEFAULT_SKU_UNIT).toLowerCase(),
    }
  }

  return { amount: trimmed, unit: DEFAULT_SKU_UNIT }
}

/** Build canonical SKU string, preserving optional prefix e.g. "Big bags 1250 kg". */
export function formatSku(
  amount: string,
  unit: string = DEFAULT_SKU_UNIT,
  prefix?: string
): string | undefined {
  const normalized = amount.replace(/,/g, '').trim()
  if (!normalized) return prefix?.trim() || undefined

  const unitSuffix = unit.toLowerCase()

  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    if (/\s*kg\s*$/i.test(normalized)) {
      return prefix ? `${prefix} ${normalized}`.trim() : normalized
    }
    const combined = `${normalized} ${unitSuffix}`.trim()
    return prefix ? `${prefix} ${combined}`.trim() : combined
  }

  const num = parseFloat(normalized)
  if (Number.isNaN(num) || num < 0) return undefined

  const displayAmount = Number.isInteger(num) ? String(Math.round(num)) : String(num)
  const weight = `${displayAmount} ${unitSuffix}`
  return prefix ? `${prefix} ${weight}`.trim() : weight
}

/** Normalize any SKU input before save. */
export function normalizeSkuForSave(value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined
  const cleaned = collapseTrailingKg(value)
  const { amount, unit, prefix } = parseSku(cleaned)
  return formatSku(amount, unit, prefix) ?? cleaned
}

/** Display SKU in tables and detail views. */
export function formatSkuDisplay(value: string | null | undefined): string {
  if (!value?.trim()) return '—'
  const normalized = normalizeSkuForSave(value)
  return normalized ?? value.trim()
}

/** Strip currency code/symbols so price is stored as a plain number string. */
export function sanitizePriceForSave(value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined
  const cleaned = value
    .trim()
    .replace(/^(USD|ZAR|EUR|CAD|GBP)\s*/i, '')
    .replace(/^[$€£R]+\s*/, '')
    .replace(/,/g, '')
    .trim()
  return cleaned || undefined
}

/** Load price into numeric input (no currency symbols). */
export function parsePriceForInput(value: string | null | undefined): string {
  return sanitizePriceForSave(value) ?? ''
}
